import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { SapService } from 'src/app/services/sap/sap.service';

/**
 * Centro de control SAP ("Integración SAP") — Parámetros de Sistema.
 * Pestañas: Resumen · Clientes · Configuración.
 *
 * Al tocar un cliente se abre un DRAWER a la DERECHA con dos sub-pestañas:
 *   Información → estado + acciones (Analizar / Crear / Modificar con formulario)
 *   Bitácora    → historial del cliente; cada entrada se expande con el request
 *                 y la respuesta de SAP.
 */
const PROFESIONES: { code: string; label: string }[] = [
  { code: '0001', label: 'Negocio propio' },
  { code: '0002', label: 'Profesional independiente' },
  { code: '0003', label: 'Empleado' },
  { code: '0004', label: 'Jubilado' },
  { code: '0005', label: 'Pensionado' },
  { code: '0006', label: 'Estudiante' },
  { code: '0007', label: 'Ama de casa' },
  { code: '0008', label: 'Desempleado' },
];

@Component({
  selector: 'app-sap-hub',
  templateUrl: './sap-hub.component.html',
  styleUrls: ['./sap-hub.component.css'],
})
export class SapHubComponent implements OnInit, OnDestroy {
  tab: 'resumen' | 'clientes' | 'config' = 'resumen';
  profesiones = PROFESIONES;

  // ---- Resumen ----
  kpis: any = null;
  salud: any = null;
  erroresMotivo: any[] = [];
  calculando = false;
  loadingKpis = false;
  calculadoAt: string | null = null;
  private pollTimer: any = null;

  // ---- Clientes ----
  clientStatus: 'todos' | 'creados' | 'error' | 'sin_registrar' = 'creados';
  clientErrorTipo: '' | 'DATOS' | 'TRANSITORIO' = ''; // sub-filtro dentro de "Con error"
  search = '';
  clients: any[] = [];
  clientsTotal: number | null = 0;
  page = 1;
  limit = 25;
  loadingClients = false;

  // ---- Reportes de la pestaña Clientes (export): creados, o con error si el filtro es "Con error" ----
  reportInitDate = '';
  reportEndDate = '';
  downloadingReport = false;

  // ---- Config ----
  config: any = null;
  loadingConfig = false;
  configEdit: any = null;                // modelo editable de la config
  savingConfig = false;
  configMsg: { ok: boolean; text: string } | null = null;

  // ---- Drawer ----
  drawerOpen = false;
  drawerTab: 'info' | 'bitacora' = 'info';
  sel: any = null;
  selLogs: any[] = [];
  loadingSelLogs = false;
  expandedLog: any = null;         // log expandido (request/response)
  selAnalysis: any = null;         // { apto, errores }
  payload: any = null;             // payload construido (para el formulario)
  loadingPayload = false;
  showForm = false;                // muestra el formulario de modificación
  edit: any = null;                // modelo editable
  actionBusy = false;
  actionMsg: { ok: boolean; text: string } | null = null;

  constructor(private sap: SapService, private router: Router) {}

  ngOnInit(): void { this.loadKpis(); }
  ngOnDestroy(): void { if (this.pollTimer) clearTimeout(this.pollTimer); if (this.searchTimer) clearTimeout(this.searchTimer); }

  setTab(t: any): void {
    this.tab = t;
    if (t === 'clientes' && this.clients.length === 0) this.loadClients();
    if (t === 'config' && !this.config) this.loadConfig();
  }

  // ===================== RESUMEN =====================
  async loadKpis(refresh = false): Promise<void> {
    this.loadingKpis = true;
    try {
      const r = await this.sap.getKpis(refresh);
      this.kpis = r?.kpis || null;
      this.salud = r?.salud || null;
      this.erroresMotivo = r?.errores_por_motivo || [];
      this.calculando = !!(r?.kpis?.calculando || r?.calculando);
      this.calculadoAt = r?.calculado_at || null;
      if (this.pollTimer) clearTimeout(this.pollTimer);
      if (this.calculando) this.pollTimer = setTimeout(() => this.loadKpis(false), 5000);
    } catch (e) { this.kpis = null; }
    finally { this.loadingKpis = false; }
  }
  refreshKpis(): void { this.loadKpis(true); }

  // ===================== CLIENTES =====================
  async loadClients(page = 1): Promise<void> {
    this.loadingClients = true; this.page = page;
    try {
      const r = await this.sap.listClients({ status: this.clientStatus, search: this.search.trim(), page, limit: this.limit, error_tipo: this.clientStatus === 'error' ? this.clientErrorTipo : '' });
      this.clients = r?.clients || [];
      this.clientsTotal = r?.total !== undefined ? r.total : null;
    } catch (e) { this.clients = []; this.clientsTotal = null; }
    finally { this.loadingClients = false; }
  }
  changeStatus(s: any): void { this.clientStatus = s; if (s !== 'error') this.clientErrorTipo = ''; this.page = 1; this.loadClients(1); }
  changeErrorTipo(t: any): void { this.clientErrorTipo = t; this.page = 1; this.loadClients(1); }
  buscar(): void { if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null; } this.page = 1; this.loadClients(1); }
  private searchTimer: any = null;
  /** Búsqueda mientras se escribe (debounce 400ms), sin necesidad de botón. */
  onSearchInput(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => { this.searchTimer = null; this.page = 1; this.loadClients(1); }, 400);
  }
  limpiarBusqueda(): void { this.search = ''; this.page = 1; this.loadClients(1); }

  // ---- Export del reporte de clientes creados en SAP ----
  async exportReport(): Promise<void> {
    if (!this.reportInitDate || !this.reportEndDate) return;
    if (this.reportInitDate > this.reportEndDate) {
      Swal.fire('Rango inválido', 'La fecha "Desde" no puede ser mayor que "Hasta".', 'warning');
      return;
    }
    await this.doExport(this.reportInitDate, this.reportEndDate);
  }

  async exportAllReport(): Promise<void> {
    let html: string;
    if (this.clientStatus === 'error') {
      html = 'Vas a exportar <b>todo el historial</b> de errores de SAP. Puede tardar un poco.';
    } else {
      const n = this.kpis?.creados;
      const cuantos = (n != null) ? `${Number(n).toLocaleString('es')} clientes` : 'todos los clientes';
      html = `Vas a exportar <b>${cuantos}</b> creados en SAP. Según la cantidad, el archivo puede tardar y pesar.`;
    }
    const r = await Swal.fire({
      icon: 'warning',
      title: '¿Exportar todos?',
      html,
      showCancelButton: true,
      confirmButtonText: 'Sí, exportar todos',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
    });
    if (!r.isConfirmed) return;
    await this.doExport(undefined, undefined);
  }

  private async doExport(initDate?: string, endDate?: string): Promise<void> {
    this.downloadingReport = true;
    Swal.fire({
      title: 'Generando el reporte…',
      html: 'Preparando el archivo Excel, un momento.',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      if (this.clientStatus === 'error') await this.sap.downloadErrorsReport(initDate, endDate);
      else await this.sap.downloadCreatedReport(initDate, endDate);
      Swal.fire({ icon: 'success', title: 'Reporte descargado', timer: 1800, showConfirmButton: false });
    } catch (e: any) {
      Swal.fire('Error', 'No se pudo generar el reporte: ' + (e?.message || 'error'), 'error');
    } finally {
      this.downloadingReport = false;
    }
  }
  get totalPages(): number { return this.clientsTotal == null ? 1 : Math.max(1, Math.ceil(this.clientsTotal / this.limit)); }
  nextPage(): void { if (this.page < this.totalPages) this.loadClients(this.page + 1); }
  prevPage(): void { if (this.page > 1) this.loadClients(this.page - 1); }

  // "Creado" = está en SAP (tiene fe_c / en_sap), aunque después un modify le falle.
  estadoLabel(c: any): string { return (c?.en_sap || c?.fe_c) ? 'Creado' : (c?.status === 'ERROR' ? 'Con error' : 'Sin registrar'); }
  estadoClass(c: any): string { return (c?.en_sap || c?.fe_c) ? 'badge-ok' : (c?.status === 'ERROR' ? 'badge-err' : 'badge-pend'); }
  /** Está en SAP pero un modify posterior falló → se muestra "Creado" + aviso de sync. */
  errorDeSync(c: any): boolean { return !!((c?.en_sap || c?.fe_c) && (c?.status === 'ERROR' || c?.error_tipo)); }
  hayError(c: any): boolean { return !!(c?.error_tipo || c?.status === 'ERROR'); }

  // Taxonomía REAL del error (leída del mensaje de SAP), no un "Faltan datos" genérico.
  tipoErrorLabel(c: any): string {
    const le = (c?.last_error || '').toString();
    if (/A_AddressEmailAddressType|Resource not found for segment/i.test(le)) return 'Rechazo SAP (estructura)';
    if (/fuera de horario/i.test(le)) return 'Fuera de horario';
    if (/ya existe/i.test(le)) return 'Ya existe';
    if (c?.error_tipo === 'DATOS') return 'Datos incompletos';
    if (c?.error_tipo === 'TRANSITORIO') return 'Temporal (reintenta)';
    return c?.error_tipo || (c?.status === 'ERROR' ? 'Error SAP' : '');
  }
  tipoErrorClass(c: any): string {
    const le = (c?.last_error || '').toString();
    if (/fuera de horario/i.test(le) || c?.error_tipo === 'TRANSITORIO') return 'badge-pend';
    return 'badge-err';
  }

  // ===================== DRAWER =====================
  openDrawer(c: any): void {
    this.sel = { ...c };
    this.drawerTab = 'info';
    this.selAnalysis = null; this.actionMsg = null; this.showForm = false;
    this.payload = null; this.edit = null; this.expandedLog = null;
    this.drawerOpen = true;
    this.loadSelLogs();
    this.loadPayload();  // trae el payload construido (para análisis y formulario)
  }
  closeDrawer(): void {
    this.drawerOpen = false; this.sel = null; this.selLogs = [];
    this.selAnalysis = null; this.payload = null; this.edit = null;
    this.showForm = false; this.actionMsg = null; this.expandedLog = null;
  }

  async loadPayload(): Promise<void> {
    if (!this.sel) return;
    this.loadingPayload = true;
    try {
      const r = await this.sap.analyze(this.sel.code);
      this.payload = r?.payload || null;
      this.selAnalysis = { apto: r?.apto, aprobado: r?.aprobado, errores: r?.errores || [] };
      this.buildEditModel();
    } catch (e) { this.payload = null; }
    finally { this.loadingPayload = false; }
  }

  async loadSelLogs(): Promise<void> {
    if (!this.sel) return;
    this.loadingSelLogs = true;
    try {
      const r = await this.sap.logs(this.sel.code, 50);
      this.selLogs = r?.logs || [];
    } catch (e) { this.selLogs = []; }
    finally { this.loadingSelLogs = false; }
  }

  toggleLog(l: any): void { this.expandedLog = this.expandedLog === l ? null : l; }
  pretty(v: any): string { try { return typeof v === 'string' ? v : JSON.stringify(v, null, 2); } catch { return String(v); } }

  get selCreado(): boolean { return !!(this.sel && this.sel.fe_c); }

  /** Cuentas bancarias que van en el request de modificación (editables en línea; el backend las valida). */
  get modifyBanks(): any[] { return (this.payload && this.payload.d && this.payload.d.to_BusinessPartnerBank && this.payload.d.to_BusinessPartnerBank.results) || []; }

  private buildEditModel(): void {
    const d = this.payload?.d;
    if (!d) { this.edit = null; return; }
    const addr = (d.to_BusinessPartnerAddress?.results || [])[0] || {};
    const email = (addr.to_EmailAddress?.results || [])[0] || {};
    const phones = addr.to_PhoneNumber?.results || [];
    this.edit = {
      birthDate: (d.BirthDate || '').substring(0, 10),
      BusinessPartnerOccupation: d.BusinessPartnerOccupation || '',
      NaturalPersonEmployerName: d.NaturalPersonEmployerName || '',
      EmailAddress: email.EmailAddress || '',
      phone1: phones[0]?.PhoneNumber || '',
      phone2: phones[1]?.PhoneNumber || '',
    };
  }

  private buildModifyPayload(): any {
    const e = this.edit;
    const base = JSON.parse(JSON.stringify(this.payload.d));
    base.BirthDate = e.birthDate ? `${e.birthDate}T00:00:00` : '';
    base.BusinessPartnerOccupation = e.BusinessPartnerOccupation;
    base.NaturalPersonEmployerName = (e.NaturalPersonEmployerName || '').toUpperCase();
    const addr = (base.to_BusinessPartnerAddress?.results || [])[0] || {};
    if (!addr.to_EmailAddress) addr.to_EmailAddress = { results: [] };
    addr.to_EmailAddress.results = [{ OrdinalNumber: '1', IsDefaultEmailAddress: true, EmailAddress: e.EmailAddress }];
    const phones: any[] = [];
    if (e.phone1) phones.push({ OrdinalNumber: '1', IsDefaultPhoneNumber: true, PhoneNumber: e.phone1, PhoneNumberType: '1' });
    if (e.phone2) phones.push({ OrdinalNumber: String(phones.length + 1), IsDefaultPhoneNumber: false, PhoneNumber: e.phone2, PhoneNumberType: '1' });
    if (phones.length === 1) phones.push({ OrdinalNumber: '2', IsDefaultPhoneNumber: false, PhoneNumber: phones[0].PhoneNumber, PhoneNumberType: '1' });
    addr.to_PhoneNumber = { results: phones };
    base.to_BusinessPartnerAddress = { results: [addr] };
    if (base.to_BusinessPartnerBank && !(base.to_BusinessPartnerBank.results || []).length) delete base.to_BusinessPartnerBank;
    return { d: base };
  }

  async drawerAnalizar(): Promise<void> {
    if (!this.sel || this.actionBusy) return;
    this.actionBusy = true; this.actionMsg = null;
    try { await this.loadPayload(); }
    finally { this.actionBusy = false; this.loadSelLogs(); }
  }

  async drawerCrear(): Promise<void> {
    if (!this.sel || this.actionBusy || this.selAnalysis?.aprobado === false) return;
    if (this.selCreado) {
      const c = await Swal.fire({
        icon: 'question', title: '¿Reenviar el alta a SAP?',
        html: `Este cliente ya figura como creado en SAP (BP <b>${this.sel.business_partner || '—'}</b>).<br>Si SAP responde que ya existe, se mantiene como creado.`,
        showCancelButton: true, confirmButtonText: 'Sí, reenviar', cancelButtonText: 'Cancelar', confirmButtonColor: '#2563eb',
      });
      if (!c.isConfirmed) return;
    }
    const estabaCreado = this.selCreado;
    this.actionBusy = true; this.actionMsg = null;
    try {
      const r = await this.sap.create(this.sel.code);
      if (r?.success) {
        this.sel.business_partner = r.data?.business_partner || this.sel.business_partner;
        this.sel.fe_c = r.data?.fe_c || new Date().toISOString();
        this.sel.status = 'CREATED'; this.sel.last_error = null;
        this.actionMsg = { ok: true, text: r.message || 'Cliente creado en SAP.' };
      } else {
        // El back no marca error a un cliente no aprobado ni a uno ya creado.
        if (!estabaCreado && r?.reason !== 'NOT_APPROVED') { this.sel.status = 'ERROR'; this.sel.last_error = r?.message || 'No se pudo crear.'; }
        this.actionMsg = { ok: false, text: r?.message || 'No se pudo crear en SAP.' };
      }
    } catch (e: any) {
      this.actionMsg = { ok: false, text: e?.error?.message || 'No se pudo crear en SAP.' };
    } finally { this.actionBusy = false; this.loadSelLogs(); this.loadClients(this.page); }
  }

  abrirFormModificar(): void { this.showForm = true; this.actionMsg = null; if (!this.edit) this.buildEditModel(); }
  cancelarForm(): void { this.showForm = false; this.buildEditModel(); }

  async guardarModificacion(): Promise<void> {
    if (!this.sel || this.actionBusy || !this.payload) return;
    this.actionBusy = true; this.actionMsg = null;
    try {
      const r = await this.sap.modify(this.sel.code, this.buildModifyPayload());
      if (r?.success) {
        this.sel.fe_m = r.data?.fe_m || new Date().toISOString();
        this.sel.status = 'CREATED';
        this.actionMsg = { ok: true, text: r.message || 'Cliente modificado en SAP.' };
        this.showForm = false;
      } else {
        this.actionMsg = { ok: false, text: r?.message || 'No se pudo modificar en SAP.' };
      }
    } catch (e: any) {
      this.actionMsg = { ok: false, text: e?.error?.message || 'No se pudo modificar en SAP.' };
    } finally { this.actionBusy = false; this.loadSelLogs(); this.loadClients(this.page); }
  }

  abrirFicha(): void { if (this.sel) this.router.navigate(['/console/person/sap', this.sel.code]); }

  // ===================== CONFIG =====================
  async loadConfig(): Promise<void> {
    this.loadingConfig = true;
    this.configMsg = null;
    try {
      const r = await this.sap.getConfig();
      this.config = r?.config || null;
      this.configEdit = this.config ? {
        base_url: this.config.base_url || '',
        service_path: this.config.service_path || '',
        url_csrf: this.config.url_csrf || '',
        url_create: this.config.url_create || '',
        url_modify: this.config.url_modify || '',
        method_create: this.config.method_create || 'POST',
        method_modify: this.config.method_modify || 'PATCH',
        use_csrf: !!this.config.use_csrf,
        enabled: !!this.config.enabled,
        auth_user: this.config.auth_user || '',
        auth_pass: '',   // vacío = mantener la guardada
        // Interruptores + programación del cron
        crear_activo: this.config.crear_activo !== false,
        modificar_activo: this.config.modificar_activo !== false,
        cron_activo: this.config.cron_activo !== false,
        cron_hora: Number.isInteger(this.config.cron_hora) ? this.config.cron_hora : 22,
        cron_frecuencia_horas: this.config.cron_frecuencia_horas > 0 ? this.config.cron_frecuencia_horas : 24,
      } : null;
    } catch (e) { this.config = null; this.configEdit = null; }
    finally { this.loadingConfig = false; }
  }

  /** Regenera las 3 URLs a partir de la URL base + service_path (útil al cambiar de entorno). */
  regenerarUrls(): void {
    if (!this.configEdit) return;
    const base = (this.configEdit.base_url || '').replace(/\/+$/, '');
    const svc = this.configEdit.service_path || '/sap/opu/odata/sap/API_BUSINESS_PARTNER';
    this.configEdit.url_csrf = `${base}${svc}/`;
    this.configEdit.url_create = `${base}${svc}/A_BusinessPartner`;
    this.configEdit.url_modify = `${base}${svc}/A_BusinessPartner('{bp}')`;
  }

  async saveConfig(): Promise<void> {
    if (!this.configEdit) return;
    this.savingConfig = true;
    this.configMsg = null;
    try {
      const patch: any = { ...this.configEdit };
      if (!patch.auth_pass) delete patch.auth_pass; // no pisar la clave guardada si está vacía
      const r = await this.sap.updateConfig(patch);
      this.config = r?.config || this.config;
      this.configMsg = { ok: true, text: 'Configuración guardada.' };
      await this.loadConfig();
    } catch (e: any) {
      this.configMsg = { ok: false, text: e?.error?.message || 'No se pudo guardar la configuración.' };
    } finally {
      this.savingConfig = false;
    }
  }
}
